import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Cpu, Zap, Play, Layers, Compass, Globe, Sparkles, Check, 
  HelpCircle, Shield, ArrowRight, RefreshCw, Activity, Terminal, 
  Settings, Key, Code, Database, TrendingUp, AlertTriangle, MessageSquare,
  Mic, Trash2, Volume2, Save, Headphones, Download, Radio, FileAudio, Square
} from "lucide-react";
import { MasterEdgeHubEngine } from '../lib/NexoraxEdgeHub';
import { useNexoraXLedger } from "../lib/NexoraXLedgerManager";
import { AudioVisualizerCanvas } from "./AudioVisualizerCanvas";
import { NexoraXMultiAgentNodeDeck } from "./NexoraXMultiAgentNodeDeck";
import { NexoraXIntegrationSandboxHub } from "./NexoraXIntegrationSandboxHub";

interface PipelineNode {
  id: string;
  name: string;
  type: 'crawler' | 'vetting' | 'accounting' | 'proxy' | 'guardian' | 'strike';
  status: 'IDLE' | 'ACTIVE' | 'PROCESSING' | 'SUCCESS';
  frequency: number;
  connectionCount: number;
}

interface CustomLead {
  name: string;
  company: string;
  industry: string;
  revenueEst: number;
}

export function NexoraxStudio() {
  // Retrieve the NexoraX ledger manager context
  const { triggerLocalSimulationUpdate } = useNexoraXLedger();

  // Religious Preamble Standard
  const preamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Active Engines State
  const [activeEngine, setActiveEngine] = useState<'AI_ENGINE' | 'AI_GENERATOR' | 'API_KEY_ENGINE' | 'CODE_ENGINE'>('AI_ENGINE');
  
  // God-Mode funnel simulation state
  const [funnelStage, setFunnelStage] = useState<'idle' | 'trial' | 'extraction' | 'closure'>('idle');
  const [funnelCountdown, setFunnelCountdown] = useState(0); // simulation minutes counter
  const [funnelProgress, setFunnelProgress] = useState(0);
  const [funnelLogs, setFunnelLogs] = useState<string[]>([]);

  // Pipeline nodes
  const [nodes, setNodes] = useState<PipelineNode[]>([
    { id: "node-1", name: "VANGUARD-CLOSER Node 01", type: "strike", status: "ACTIVE", frequency: 185, connectionCount: 14 },
    { id: "node-2", name: "THE ORACLE Node 06", type: "crawler", status: "ACTIVE", frequency: 180, connectionCount: 22 },
    { id: "node-3", name: "THE GAVEL Node 16", type: "vetting", status: "IDLE", frequency: 220, connectionCount: 8 },
    { id: "node-4", name: "CHRONOS LEDGER Node 17", type: "accounting", status: "ACTIVE", frequency: 135, connectionCount: 31 },
    { id: "node-5", name: "CloudSync-02 Node 18.5", type: "guardian", status: "ACTIVE", frequency: 140, connectionCount: 45 },
    { id: "node-6", name: "PORT 3000 PROXY GATEWAY", type: "proxy", status: "IDLE", frequency: 300, connectionCount: 19 }
  ]);

  // Lead Generation & Proactive Prospecting
  const [customLead, setCustomLead] = useState<CustomLead>({
    name: "Alex Sterling",
    company: "Apex Healthcare Solutions",
    industry: "SME Clinical Services",
    revenueEst: 2800000
  });

  const [campaignResult, setCampaignResult] = useState<string | null>(null);
  const [isCompilingPreset, setIsCompilingPreset] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string>("node-1");

  // NexoraX Intelligence Scraper states
  const [scraperTagName, setScraperTagName] = useState<string>("Joe's Artisanal Bakeries");
  const [scraperTagLink, setScraperTagLink] = useState<string>("https://artisanalbakeries.co.uk");
  const [scraperLoading, setScraperLoading] = useState<boolean>(false);
  const [scraperLogs, setScraperLogs] = useState<string[]>([]);
  const [scraperResult, setScraperResult] = useState<{
    success: boolean;
    targetName: string;
    targetUrl: string;
    emails: string[];
    industry: string;
    analysis: string;
  } | null>(null);

  // Browser Voice Directives States
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioMemos, setAudioMemos] = useState<Array<{
    id: string;
    title: string;
    duration: string;
    date: string;
    size: string;
    status: 'CACHED_LOCAL' | 'MIRRORED_TO_VAULT';
    url: string;
  }>>([
    {
      id: "memo-pre-1",
      title: "MIGRATION_MANDATE_Staff Coordinator_SC887095.wav",
      duration: "01:42",
      date: "2026-05-28 18:04:15",
      size: "2.5 MB",
      status: "MIRRORED_TO_VAULT",
      url: ""
    },
    {
      id: "memo-pre-2",
      title: "ACCESS_TO_WORK_Secure Invoice Register_GRANT_CLAUSE.wav",
      duration: "02:15",
      date: "2026-05-29 11:30:22",
      size: "3.2 MB",
      status: "MIRRORED_TO_VAULT",
      url: ""
    }
  ]);

  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [currentMemoUrl, setCurrentMemoUrl] = useState<string | null>(null);
  const [vaultProcessing, setVaultProcessing] = useState<string | null>(null);
  const [customMemoTitle, setCustomMemoTitle] = useState("DIRECTIVE_MEMO_03");
  const [waveHeights, setWaveHeights] = useState<number[]>(Array(24).fill(15));

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Mapped simulation of live microphone visualizer
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRecording) {
      interval = setInterval(() => {
        setWaveHeights(Array.from({ length: 24 }, () => Math.floor(Math.random() * 35) + 5));
      }, 100);
    } else {
      setWaveHeights(Array(24).fill(15));
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (micStream) {
        micStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [micStream]);

  // Handle Recording triggers
  const startRecording = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicStream(stream);

        const recorder = new MediaRecorder(stream);
        const chunks: Blob[] = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/wav' });
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setCurrentMemoUrl(url);
        };

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
        setRecordingTime(0);

        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        alert("Audio recording interface is not supported or accessible inside this frame wrapper. Open in a new tab to capture microphone streams.");
      }
    } catch (err) {
      console.error("Microphone stream error:", err);
      alert("Microphone stream acquisition failed. Please guarantee browser permissions for microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (micStream) {
        micStream.getTracks().forEach(track => track.stop());
        setMicStream(null);
      }
    }
  };

  const formatSecs = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const saveMemoToLocal = () => {
    if (!audioBlob || !currentMemoUrl) return;

    const sizeInMB = (audioBlob.size / (1024 * 1024)).toFixed(1);
    const durationStr = formatSecs(recordingTime);
    const dateStr = new Date().toISOString().replace('T', ' ').substring(0, 19);

    const newMemo = {
      id: `memo-${Date.now()}`,
      title: `${customMemoTitle.toUpperCase().trim().replace(/[^A-Z0-9_]/gi, '_') || 'DIRECTIVE_MD'}.wav`,
      duration: durationStr,
      date: dateStr,
      size: `${sizeInMB !== "0.0" ? sizeInMB : "0.1"} MB`,
      status: 'CACHED_LOCAL' as const,
      url: currentMemoUrl
    };

    setAudioMemos(prev => [newMemo, ...prev]);
    setAudioBlob(null);
    setCurrentMemoUrl(null);
    setCustomMemoTitle(`DIRECTIVE_MEMO_${audioMemos.length + 1}`);
  };

  const deleteMemo = (id: string) => {
    setAudioMemos(prev => prev.filter(m => m.id !== id));
  };

  const mirrorToImperialVault = (id: string) => {
    setVaultProcessing(id);
    setTimeout(() => {
      setAudioMemos(prev => prev.map(m => {
        if (m.id === id) {
          return { ...m, status: 'MIRRORED_TO_VAULT' as const };
        }
        return m;
      }));
      setVaultProcessing(null);
    }, 1800);
  };

  // Run the 15-2-8 God-Mode Funnel Extraction Program (Simulated Loop)
  const startGodModeFunnel = () => {
    setFunnelStage('trial');
    setFunnelProgress(0);
    setFunnelCountdown(15);
    setFunnelLogs(["[GOD PROTOCOL INIT] Triggering 15-minute trial framework countdown..."]);

    // Phase transitions
    setTimeout(() => {
      setFunnelStage('extraction');
      setFunnelCountdown(2);
      setFunnelProgress(50);
      setFunnelLogs(prev => [
        `[GOD PROTOCOL] 15m trial completed. Advancing directly to Phase 2: Core Data Extraction...`,
        `[EXTRACTION] Scanning client infrastructure directories behind port 3000...`,
        `[EXTRACTION] Audits locked. Initializing backup mirroring to Google Drive NexoraX Resource Library via CloudSync-02...`,
        ...prev
      ]);
    }, 4000);

    setTimeout(() => {
      setFunnelStage('closure');
      setFunnelCountdown(8);
      setFunnelProgress(85);
      setFunnelLogs(prev => [
        `[GOD PROTOCOL] Core extraction complete. Deploying final 8-minute Critical Closure Gavel Strike...`,
        `[FINANCIAL] Allocating 1% 3% Community Allocation (Charity 1 Scottish target, Charity 2 Global, Charity 3 UK)...`,
        `[FINANCIAL] Deduced 10% Maintenance & Hosting Fee. Redirecting main settlements directly to Secure Payment Provider: 04-**-** | ******15...`,
        ...prev
      ]);
    }, 8500);

    setTimeout(() => {
      setFunnelStage('idle');
      setFunnelCountdown(0);
      setFunnelProgress(100);
      setFunnelLogs(prev => [
        `[SUCCESS] 15-2-8 Funnel successfully resolved. Customer has been securely onboarded. Operational handshakes clear!`,
        `[FINANCIAL] Dynamic increment of £150,000.00 added to Gross Ticker.`,
        ...prev
      ]);
      // Link Stage 3 completion to increment the Gross Harvest ticker in the header
      triggerLocalSimulationUpdate(150000);
    }, 13000);
  };

  // Compile active system configurations in Studio
  const handleCompilePreset = () => {
    setIsCompilingPreset(true);
    setTimeout(() => {
      setIsCompilingPreset(false);
      // Trigger Random Status updates
      setNodes(prev => prev.map(n => ({
        ...n,
        status: Math.random() > 0.45 ? 'ACTIVE' : 'PROCESSING',
        frequency: n.frequency + Math.floor(Math.random() * 15) - 7
      })));
    }, 1200);
  };

  // Generate Strike Pitch for Campaign
  const handleGeneratePitch = () => {
    const pitchText = `CRITICAL DIRECTIVE ISSUED TO ${customLead.company.toUpperCase()}:
[BEGIN MANDATE]
Hi ${customLead.name},
I am initializing the NexoraX Digital deployment for our enterprise partners this week. We have identified ${customLead.company} as a priority transformation candidate under SC887095 protocols.

Please clear the system activation invoice to secure your priority onboarding slot and link your infrastructure immediately:
https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00

Support frequency: 08000 622827
Registered HQ: SC887095 (Secure Payment Provider: 04-**-** | ******15)
[END MANDATE]`;
    setCampaignResult(pitchText);
    MasterEdgeHubEngine.archiveStrikePitch(customLead.name, customLead.company, pitchText, "Stripe Link");
  };

  // NexoraX Intelligence Network Scraper & Audit analyzer
  const handleTriggerScraper = async () => {
    if (!scraperTagName && !scraperTagLink) {
      alert("Please provide either a valid Target Name or website Resource link to scan.");
      return;
    }

    setScraperLoading(true);
    setScraperResult(null);
    setScraperLogs(["[SYS-INIT] Channeling secure ports..."]);

    const simProgressLogs = [
      "[PROXY] Routing socket tunnel behind standard Port 3000 ingress filters...",
      "[AUTHENTICATION] Key validation secured vs. API_KEY_ENGINE parameters.",
      "[CRAWLER] Vanguard-Closer initiated. Siphoning target DOM components...",
      "[EXTRACTOR] Oracle-06 scanning index routes. Intercepting contact relays...",
      "[INTEL] Extracting email footprints and domain registrations...",
      "[COGNITIVE-DEEP] Pushing siphoned text logs to Gemini-2.5 multi-model network...",
      "[STRATEGY] Negotiating Gavel-16 structural tax deductions and enhancements...",
      "[IMPERIAL-RECON] Syncing generated tactical audit reports with Google Drive...",
      "[SYSTEM-SYNC] Compiling custom revenue rating scales..."
    ];

    let currentLogOffset = 0;
    const logInterval = setInterval(() => {
      if (currentLogOffset < simProgressLogs.length) {
        setScraperLogs(prev => [...prev, simProgressLogs[currentLogOffset]]);
        currentLogOffset++;
      }
    }, 450);

    try {
      const response = await fetch("/api/scraper/analyze", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: scraperTagName,
          link: scraperTagLink
        })
      });

      const parsedData = await response.json();
      clearInterval(logInterval);

      if (parsedData.status === "success") {
        setScraperResult({
          success: true,
          targetName: parsedData.targetName,
          targetUrl: parsedData.targetUrl,
          emails: parsedData.emails || [],
          industry: parsedData.industry,
          analysis: parsedData.analysis
        });
        setScraperLogs(prev => [
          ...prev, 
          `[SUCCESS] Intelligence analysis complete. Standard preambles locked.`
        ]);
      } else {
        alert(parsedData.message || "Scraper failed to capture intelligence details.");
      }
    } catch (err) {
      clearInterval(logInterval);
      console.error(err);
      alert("A server connectivity issue interrupted the real-time scraper node.");
    } finally {
      setScraperLoading(false);
    }
  };

  return (
    <div id="nexorax-studio-tab" className="w-full max-w-7xl mx-auto space-y-12 text-slate-300 font-sans relative pb-20">
      
      {/* Absolute Preamble Header */}
      <div className="text-center font-mono text-[10px] md:text-xs text-cyan-400 font-black tracking-[0.25em] uppercase select-none opacity-80 max-w-4xl mx-auto border-b border-cyan-500/10 pb-4">
        {preamble}
      </div>

      {/* Hero Headline visual cards */}
      <div className="relative rounded-3xl p-8 md:p-12 overflow-hidden bg-[#02050f] border border-cyan-500/20 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 max-w-4xl space-y-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-500/10 border border-cyan-400/20 rounded-full text-cyan-400 text-[9px] font-mono tracking-widest font-black uppercase">
            <Cpu className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '4s' }} />
            <span>NEXORAX LIVING CORE NEURAL ENGINE STUDIO</span>
          </div>
          
          <h2 className="text-4xl md:text-7xl font-sans font-black text-white tracking-tighter uppercase leading-none">
            NEXORAX <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-400 to-[#e840f3]">STUDIO ENGINE</span>
          </h2>
          
          <p className="font-mono text-[11px] md:text-xs text-slate-400 uppercase tracking-widest max-w-3xl leading-relaxed">
            Configure system microservices, control core computational pipelines, orchestrate simulated 15-2-8 god-mode sales funnels, and design customized outreach campaigns. This is the supreme operating framework designed strictly to optimize enterprise speed, NexoraX ledger integrity, and global compliance.
          </p>
        </div>
      </div>

      {/* Grid: Sector 1: Master Systems Dashboard & Nodes Grid, Sector 2: The Core 4 Engines */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        
        {/* LEADING COGNITIVE ENGINE PRESETS (Col-span 5) */}
        <div className="lg:col-span-5 flex flex-col space-y-6">
          <div className="bg-[#02050e] border border-slate-900 rounded-3xl p-6 flex flex-col justify-between h-full relative overflow-hidden space-y-6">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

            <div className="space-y-4">
              <div className="border-b border-slate-900 pb-3">
                <div className="text-cyan-400 font-bold text-[8px] uppercase tracking-[0.3em] font-mono">
                  ACTIVE CORE RESOURCE MODULES
                </div>
                <h3 className="text-white text-md font-sans font-extrabold uppercase mt-1 tracking-tight">
                  THE ENGINE CORE REGISTRY
                </h3>
              </div>

              {/* Engine Selectors list */}
              <div className="space-y-3 font-mono">
                {[
                  { key: 'AI_ENGINE', title: 'AI_ENGINE (COGNITIVE CORE)', desc: 'Governs natural language dialog routines. Automatically maintains our strict 0.8s SLA response latency.', icon: <MessageSquare className="w-4 h-4 text-cyan-400" /> },
                  { key: 'AI_GENERATOR', title: 'AI_GENERATOR (MEDIA CORE)', desc: 'Generates creative digital models, high-performance assets, and visually polished layout specifications.', icon: <Sparkles className="w-4 h-4 text-[#e840f3]" /> },
                  { key: 'API_KEY_ENGINE', title: 'API_KEY_ENGINE (METRIC SECURE)', desc: 'Locks, throttles, and meters client API endpoints to secure persistent token validation against scraping bots.', icon: <Key className="w-4 h-4 text-amber-400" /> },
                  { key: 'CODE_ENGINE', title: 'CODE_ENGINE (SYS ARCHITECT)', desc: 'Compiles, lints, and serves custom container parameters behind port 3000 standard reverse proxy routes.', icon: <Code className="w-4 h-4 text-emerald-400" /> }
                ].map((eng) => {
                  const isActive = activeEngine === eng.key;
                  return (
                    <motion.div
                      key={eng.key}
                      onClick={() => setActiveEngine(eng.key as any)}
                      animate={isActive ? {
                        scale: [1, 1.015, 1],
                        borderColor: ["rgba(6, 182, 212, 0.4)", "rgba(232, 64, 243, 0.4)", "rgba(6, 182, 212, 0.4)"],
                        boxShadow: [
                          "0 0 10px rgba(0, 195, 255, 0.1)",
                          "0 0 20px rgba(232, 64, 243, 0.25)",
                          "0 0 10px rgba(0, 195, 255, 0.1)"
                        ]
                      } : {
                        scale: 1,
                        borderColor: "rgba(15, 23, 42, 1)",
                        boxShadow: "none"
                      }}
                      transition={isActive ? {
                        repeat: Infinity,
                        duration: 3.5,
                        ease: "easeInOut"
                      } : undefined}
                      className={`p-3 rounded-xl border transition-all duration-300 cursor-pointer ${
                        isActive 
                          ? 'bg-cyan-950/20' 
                          : 'bg-black/35 border-slate-950 hover:border-slate-850 hover:border-slate-800'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={`p-2 rounded-lg ${isActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-950 text-slate-500'}`}>
                          {eng.icon}
                        </span>
                        <div>
                          <span className="block text-[10px] font-black text-white uppercase tracking-wider">{eng.title}</span>
                          <span className="block text-[8.5px] text-slate-400 uppercase leading-relaxed mt-1">{eng.desc}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Active Core parameters view */}
            <div className="bg-black/80 border border-slate-950 p-4 rounded-xl space-y-3 font-mono text-[9px] text-slate-400">
              <span className="text-slate-500 font-bold uppercase tracking-widest block font-bold">CORE META DIRECTIVE STATE</span>
              
              {activeEngine === 'AI_ENGINE' && (
                <div className="space-y-2 uppercase leading-relaxed text-slate-300">
                  <div className="flex justify-between"><span>Active Threads:</span> <span className="text-cyan-400 font-bold">128 Concurrent</span></div>
                  <div className="flex justify-between"><span>Maximum Latency:</span> <span className="text-cyan-400 font-bold">0.8 Seconds</span></div>
                  <div className="flex justify-between"><span>Dialect parameters:</span> <span className="text-slate-400">NexoraX Authority</span></div>
                </div>
              )}

              {activeEngine === 'AI_GENERATOR' && (
                <div className="space-y-2 uppercase leading-relaxed text-slate-300">
                  <div className="flex justify-between"><span>Rendering Engine:</span> <span className="text-pink-400 font-bold">Living Brain V2</span></div>
                  <div className="flex justify-between"><span>Compression ratio:</span> <span className="text-pink-400 font-bold">98.2% Accurate</span></div>
                  <div className="flex justify-between"><span>Sourcing directory:</span> <span className="text-slate-400">/var/www/nexorax_source</span></div>
                </div>
              )}

              {activeEngine === 'API_KEY_ENGINE' && (
                <div className="space-y-2 uppercase leading-relaxed text-slate-300">
                  <div className="flex justify-between"><span>Validation check:</span> <span className="text-amber-400 font-bold">CloudSync-02 Enabled</span></div>
                  <div className="flex justify-between"><span>Key Status Limit:</span> <span className="text-amber-400 font-bold">5000 requests/min</span></div>
                  <div className="flex justify-between"><span>Vulnerability Shield:</span> <span className="text-emerald-400 font-bold">Active block</span></div>
                </div>
              )}

              {activeEngine === 'CODE_ENGINE' && (
                <div className="space-y-2 uppercase leading-relaxed text-slate-300">
                  <div className="flex justify-between"><span>Proxy standard:</span> <span className="text-emerald-400 font-bold">Strict Port 3000</span></div>
                  <div className="flex justify-between"><span>Dev commands rule:</span> <span className="text-emerald-400 font-bold">tsx server.ts</span></div>
                  <div className="flex justify-between"><span>Packaging rule:</span> <span className="text-slate-400">bundled dist/server.cjs</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* INTERATIVE NETWORKS MONITOR & STRIKE MATRIX (Col-span 7) */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
          <div className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 flex flex-col justify-between h-full space-y-6 relative min-h-[460px]">
            <div className="absolute top-3 right-4 font-mono text-[8px] text-cyan-400/55 uppercase tracking-widest flex items-center gap-1.5 select-none animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
              <span>SYSTEM INTERLOCK ONLINE</span>
            </div>

            <div className="space-y-4">
              <div className="border-b border-slate-900 pb-3">
                <div className="text-cyan-400 font-bold text-[8px] uppercase tracking-[0.3em] font-mono">
                  INTERACTIVE INFRASTRUCTURE NODES
                </div>
                <h3 className="text-white text-md font-sans font-extrabold uppercase mt-1 tracking-tight">
                  NEXORAX SECTOR COORDINATORS
                </h3>
              </div>

              {/* Nodes visualization list */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {nodes.map((node) => {
                  const isSelected = selectedNodeId === node.id;
                  const isActiveStatus = node.status === 'ACTIVE';
                  return (
                    <motion.div
                      key={node.id}
                      onClick={() => setSelectedNodeId(node.id)}
                      animate={isActiveStatus ? {
                        scale: isSelected ? [1, 1.015, 1] : [1, 1.008, 1],
                        borderColor: isSelected 
                          ? ["rgba(6, 182, 212, 1)", "rgba(16, 185, 129, 0.7)", "rgba(6, 182, 212, 1)"]
                          : ["rgba(30, 41, 59, 1)", "rgba(16, 185, 129, 0.4)", "rgba(30, 41, 59, 1)"],
                        boxShadow: isSelected 
                          ? ["0 0 12px rgba(6, 182, 212, 0.15)", "0 0 20px rgba(16, 185, 129, 0.25)", "0 0 12px rgba(6, 182, 212, 0.15)"]
                          : ["0 0 0px rgba(0,0,0,0)", "0 0 8px rgba(16, 185, 129, 0.15)", "0 0 0px rgba(0,0,0,0)"]
                      } : {
                        scale: 1,
                        borderColor: isSelected ? "rgba(6, 182, 212, 1)" : "rgba(15, 23, 42, 1)",
                        boxShadow: isSelected ? "0 0 12px rgba(6, 182, 212, 0.15)" : "none"
                      }}
                      transition={isActiveStatus ? {
                        repeat: Infinity,
                        duration: 3,
                        ease: "easeInOut"
                      } : undefined}
                      className={`p-3.5 rounded-xl border transition-all duration-300 cursor-pointer font-mono text-[9px] ${
                        isSelected 
                          ? 'bg-cyan-950/10 text-white'
                          : 'bg-black/50 border-slate-950 text-slate-400 hover:border-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-sans font-extrabold text-white text-xs uppercase">{node.name}</span>
                        {node.status === 'ACTIVE' ? (
                          <span className="px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-[7px] font-bold tracking-widest uppercase">ACTIVE</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded text-[7px] font-bold tracking-widest uppercase animate-pulse">SYNC</span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-4 text-[8px] text-slate-500 border-t border-slate-950/80 pt-2 uppercase">
                        <div>
                          <span>TARGET CODES</span>
                          <span className="block text-white font-bold">{node.type.toUpperCase()}</span>
                        </div>
                        <div>
                          <span>SIGNAL RATE</span>
                          <span className="block text-white font-bold">{node.frequency} MHz</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Dynamic node detail action */}
              {(() => {
                const sNode = nodes.find(n => n.id === selectedNodeId);
                if (!sNode) return null;
                return (
                  <div className="bg-[#00050d] border border-cyan-500/15 p-4 rounded-xl space-y-3 font-mono text-[10px] text-slate-400">
                    <div className="flex justify-between items-center text-cyan-400 border-b border-slate-950 pb-2">
                       <span className="font-bold uppercase text-[9px]">DIAGNOSTIC TELEMETRY: {sNode.name}</span>
                       <span className="text-[8px] text-slate-500">Node Speed: 0.8s SLA</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[9px] uppercase leading-relaxed text-slate-300">
                       <div>
                         <span className="block text-slate-500 text-[8px] font-extrabold pb-0.5">ESTIMATED ROUTING:</span>
                         <span>/var/www/nexorax_source <span className="text-cyan-400 font-bold">&lt;-&gt;</span> Google Drive NexoraX Resource Library</span>
                       </div>
                       <div>
                         <span className="block text-slate-500 text-[8px] font-extrabold pb-0.5">FISCAL DEBT PROTECTION:</span>
                         <span>Enforce continuous 1% 3% Community Allocation</span>
                       </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Compile System trigger button */}
            <div className="pt-2">
              <button
                onClick={handleCompilePreset}
                disabled={isCompilingPreset}
                className="w-full py-3.5 bg-gradient-to-r from-cyan-400 to-indigo-500 hover:from-cyan-300 hover:to-indigo-400 text-black font-extrabold uppercase tracking-widest text-xs rounded-xl shadow-[0_0_15px_rgba(0,195,255,0.2)] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer font-extrabold"
              >
                {isCompilingPreset ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-black" />
                    <span>COMPILING SYSTEM PRESETS & HANDSHAKES...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 text-black stroke-[2.5px]" />
                    <span>COMPILE STUDIO SYSYEM PRESETS</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* NEXORAX ADVANCED COGNITIVE MULTI-AGENT DESIGN NODE DECK WORKSPACE */}
      <NexoraXMultiAgentNodeDeck />

      {/* NEXORAX AUTOMATED CODE TEMPLATE & INTEGRATION PLAYGROUND */}
      <NexoraXIntegrationSandboxHub />

      {/* GOD-MODE 15-2-8 FUNNEL INTERFACE CONTAINER */}
      <div id="god-mode-funnel-matrix" className="bg-[#02050f] border border-[#e840f3]/25 rounded-3xl p-6 relative overflow-hidden shadow-[0_0_30px_rgba(232,64,243,0.06)]">
        <div className="absolute top-0 right-0 w-80 h-80 bg-pink-500/5 rounded-full blur-3xl pointer-events-none animate-pulse" />
        
        <div className="border-b border-slate-900 pb-4 mb-6">
          <div className="text-[#e840f3] font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <TrendingUp className="w-4 h-4 text-[#e840f3]" />
            <span>NEXORAX NEXORAX PROTOCOL: GOD-MODE FUNNEL</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase mt-1">
            THE 15-2-8 MINUTE GOD-MODE FUNNEL SIMULATOR
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1 max-w-4xl">
            Simulate Lead Director's proprietary conversion blueprint: Engage an instant 15-Minute system demo trial, execute the 2-Minute raw operational file parameters extraction loop with automatic mirror backups, and finalize the critical 8-Minute close and invoice routing directly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch font-mono text-xs">
          
          {/* STATS PANEL (Col-span 5) */}
          <div className="lg:col-span-5 bg-black/50 border border-slate-900 rounded-2xl p-5 space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <span className="text-[9px] text-[#e840f3] font-bold tracking-widest block uppercase">FUNNEL STATE METRICS</span>

              <div className="space-y-3 uppercase text-[10px]">
                {/* 15m Trial step visualizer */}
                <div className={`p-3 rounded-lg border transition-all duration-300 ${funnelStage === 'trial' ? 'bg-[#e840f3]/10 border-[#e840f3]' : 'bg-transparent border-slate-950 text-slate-500'}`}>
                  <div className="flex justify-between items-center font-bold">
                    <span>STAGE 1: 15-MINUTE TRIAL PHASE</span>
                    {funnelStage === 'trial' && <span className="text-[#e840f3] animate-pulse">ACTIVE {funnelCountdown}m</span>}
                  </div>
                  <span className="block text-[8.5px] text-slate-400 mt-1 uppercase">Engages client integration on modular sandbox layouts.</span>
                </div>

                {/* 2m Data extraction step */}
                <div className={`p-3 rounded-lg border transition-all duration-300 ${funnelStage === 'extraction' ? 'bg-cyan-950/20 border-cyan-500' : 'bg-transparent border-slate-950 text-slate-500'}`}>
                  <div className="flex justify-between items-center font-bold">
                    <span>STAGE 2: 2-MINUTE DATA EXTRACTION</span>
                    {funnelStage === 'extraction' && <span className="text-cyan-400 animate-pulse">ACTIVE {funnelCountdown}m</span>}
                  </div>
                  <span className="block text-[8.5px] text-slate-400 mt-1 uppercase">Secures backup files and copies records directly to the NexoraX Resource Library.</span>
                </div>

                {/* 8m Critical Closure step */}
                <div className={`p-3 rounded-lg border transition-all duration-300 ${funnelStage === 'closure' ? 'bg-emerald-950/15 border-emerald-500' : 'bg-transparent border-slate-950 text-slate-500'}`}>
                  <div className="flex justify-between items-center font-bold">
                    <span>STAGE 3: 8-MINUTE CRITICAL CLOSURE</span>
                    {funnelStage === 'closure' && <span className="text-emerald-400 animate-pulse">ACTIVE {funnelCountdown}m</span>}
                  </div>
                  <span className="block text-[8.5px] text-slate-400 mt-1 uppercase">Validates invoices, locks checkout, and routes to Secure Payment Provider structures.</span>
                </div>
              </div>
            </div>

            {/* Performance status card */}
            <div className="pt-2 text-[9px] text-[#e840f3] font-bold flex items-center justify-between border-t border-slate-950 pt-3">
              <span>ESTIMATED SUCCESS SCORE:</span>
              <span className="text-white text-xs font-black">99.8% EMPIRICAL SUCCESS</span>
            </div>
          </div>

          {/* SIMULATED STREAM LOG PANEL (Col-span 7) */}
          <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
            <div className="flex flex-col space-y-2 h-full">
              <span className="text-[8px] text-slate-500 font-black uppercase tracking-widest">GOD PROTOCOL PIPELINE MONITOR</span>
              
              <div className="bg-black/95 border border-slate-950 rounded-2xl p-4 h-60 overflow-y-auto custom-scrollbar space-y-2.5 font-mono text-[9px]">
                {funnelLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-slate-600 uppercase select-none space-y-2">
                    <Activity className="w-8 h-8 text-slate-800 animate-pulse" />
                    <div className="text-[9px] text-slate-500 font-extrabold tracking-widest">MONITOR STATE: SLEEP</div>
                    <span className="text-[8px] text-slate-700 max-w-sm leading-relaxed">
                      Initialize the 15-2-8 God-Mode simulation to trace multi-agent execution times, invoice generation, and Secure Payment Provider transactions.
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {funnelLogs.map((log, index) => (
                      <div 
                        key={index}
                        className={`p-2 rounded-lg border leading-relaxed ${
                          log.startsWith("[SUCCESS]") 
                            ? "bg-emerald-950/15 border-emerald-500/20 text-emerald-300 font-bold"
                            : log.startsWith("[GOD") 
                              ? "bg-purple-950/20 border-purple-500/10 text-purple-300"
                              : "bg-black border-slate-950 text-slate-300"
                        }`}
                      >
                        <div className="text-[7px] text-slate-500 border-b border-white/5 pb-1 mb-1 font-bold">
                          {new Date().toLocaleTimeString()}
                        </div>
                        <div className="uppercase tracking-wide">{log}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Core Action Trigger */}
            <button
              disabled={funnelStage !== 'idle'}
              onClick={startGodModeFunnel}
              className={`w-full py-3.5 bg-gradient-to-r from-[#e840f3] to-indigo-500 text-black font-extrabold uppercase tracking-widest text-xs rounded-xl shadow-[0_0_15px_rgba(232,64,243,0.25)] transition-all duration-300 flex items-center justify-center gap-1.5 cursor-pointer hover:shadow-[0_0_25px_rgba(232,64,243,0.5)] ${
                funnelStage !== 'idle' ? 'opacity-50 cursor-not-allowed filter grayscale' : 'hover:scale-[1.002]'
              }`}
            >
              {funnelStage !== 'idle' ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-black" />
                  <span>EXECUTING 15-2-8 GOD FUNNEL STAGES...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 fill-black text-black ml-0.5" />
                  <span>INITIALIZE GOD FUNNEL PROCESS</span>
                </>
              )}
            </button>
          </div>

        </div>
      </div>

      {/* NEXORAX IMPERIAL DIRECTIVE RECORDER (VOICE PROTOCOL-09.2) */}
      <div id="voice-directive-recorder" className="bg-[#02050f] border border-cyan-500/20 rounded-3xl p-6 relative overflow-hidden shadow-[0_0_25px_rgba(0,195,255,0.04)]">
        <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="border-b border-slate-900 pb-4 mb-6">
          <div className="text-cyan-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Radio className="w-4 h-4 text-cyan-400 animate-pulse" />
            <span>NEXORAX NEXORAX PROTOCOL: VOICE DIRECTIVES</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase mt-1">
            RECORD VOICE DIRECTIVE LAYER
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1">
            Allows Director Lead Director to capture high-authority voice memos regarding the NexoraX billing records scheme, Client Resource Workflow queues, or server VM deployments. Memos are cached locally before secure replication mapping to the Google Drive NexoraX Resource Library via CloudSync-02.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT PANEL: ACTIVE WIRE RECORDING STAGE (Col-span 5) */}
          <div className="lg:col-span-5 bg-black/60 border border-slate-900 rounded-2xl p-5 flex flex-col justify-between space-y-6">
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[9px] text-cyan-400 font-bold tracking-widest uppercase">AUDIO STREAM CONTROLS</span>
                {isRecording && (
                  <span className="flex items-center gap-1 text-[8.5px] text-red-500 font-bold tracking-wider uppercase animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                    RECORDING LIVE
                  </span>
                )}
              </div>

              {/* LIVE DIGITAL SIGNAL WAVEFORM */}
              <div className="h-20 bg-[#01030a] border border-slate-950 rounded-xl relative overflow-hidden flex items-center justify-center">
                {/* HTML5 Dynamic Real-time Audio Visualization Canvas */}
                <AudioVisualizerCanvas isRecording={isRecording} waveHeights={waveHeights} />
                
                {/* Subtle visual grid overlays */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#01030a]/5 to-transparent pointer-events-none" />
              </div>

              {/* TIME COUNTER */}
              <div className="flex items-center justify-between font-mono bg-[#030712] border border-slate-950 p-2.5 rounded-xl">
                <span className="text-[9.5px] text-slate-500 uppercase">DIRECTIVE DURATION</span>
                <span className={`text-sm font-black ${isRecording ? 'text-red-500 animate-pulse' : 'text-cyan-400'}`}>
                  {formatSecs(recordingTime)}
                </span>
              </div>

              {/* CUSTOM DIRECTIVE TITLE INPUT */}
              {!currentMemoUrl && (
                <div className="space-y-1.5 font-mono text-[9px]">
                  <label className="uppercase text-slate-500 font-bold tracking-wider">DIRECTIVE ARCHIVE FILENAME</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={customMemoTitle}
                      onChange={(e) => setCustomMemoTitle(e.target.value)}
                      disabled={isRecording}
                      className="w-full bg-[#050914] border border-slate-900 rounded-lg p-2 text-white text-[10px] uppercase font-bold focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      placeholder="DIRECTIVE_MEMO_X"
                    />
                    <span className="absolute right-2.5 top-2 text-slate-600 font-extrabold text-[8px]">.WAV</span>
                  </div>
                </div>
              )}
            </div>

            {/* ACTION DIRECT BUTTONS */}
            <div className="space-y-3">
              <div className="flex gap-3">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={!!currentMemoUrl}
                    className="flex-1 py-3 bg-[#030816] hover:bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-xl transition font-mono font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-40"
                  >
                    <Mic className="w-4 h-4 text-cyan-400" />
                    <span>ENGAGE MIC STREAM</span>
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="flex-1 py-3 bg-red-950/20 hover:bg-red-950/40 border border-red-500/40 text-red-400 rounded-xl transition font-mono font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Square className="w-3.5 h-3.5 fill-red-400 text-red-400" />
                    <span>CEASE RECORDING</span>
                  </button>
                )}
              </div>

              {/* SAVE / RETRY CURRENT AUDIO MEMO ACTIONS */}
              {currentMemoUrl && (
                <div className="bg-[#030816] border border-cyan-500/10 p-3.5 rounded-xl space-y-3 font-mono animate-fade-in">
                  <span className="text-[8.5px] text-[#e840f3] font-black uppercase block tracking-widest">
                    DIRECTIVE CAPTURED AND PROCESSED
                  </span>

                  {/* HTML5 audio player */}
                  <div className="pt-1">
                    <audio id="current-memo-player" src={currentMemoUrl} controls className="w-full h-8 rounded bg-[#01040a] text-cyan-400" />
                  </div>

                  <div className="flex gap-2.5 pt-1.5">
                    <button
                      onClick={saveMemoToLocal}
                      className="flex-1 py-2 bg-gradient-to-r from-cyan-400 to-indigo-500 hover:opacity-90 text-black font-extrabold uppercase rounded-lg text-[9px] tracking-wider flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <Save className="w-3.5 h-3.5 stroke-[2.5px]" />
                      <span>SAVE DIRECTIVE CODES</span>
                    </button>
                    <button
                      onClick={() => {
                        setCurrentMemoUrl(null);
                        setAudioBlob(null);
                      }}
                      className="py-2 px-3 bg-black hover:bg-red-950/10 border border-slate-900 hover:border-red-500/30 text-slate-400 hover:text-red-400 rounded-lg text-[9px] uppercase transition cursor-pointer"
                    >
                      <span>SCRUB</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* RIGHT PANEL: REGISTERED NEXORAX MEMOS (Col-span 7) */}
          <div className="lg:col-span-7 space-y-3 font-mono">
            <div className="flex justify-between items-center border-b border-slate-900 pb-2 mb-2">
              <span className="text-[8px] text-slate-500 font-black uppercase tracking-widest">
                VERIFIED NexoraX Resource Library INDEX
              </span>
              <span className="text-[8px] text-cyan-400 uppercase font-bold tracking-widest select-none">
                {audioMemos.length} FILE DIRECTIVES TOTAL
              </span>
            </div>

            {/* List with scrollbar */}
            <div className="space-y-3 h-72 overflow-y-auto pr-1 select-none custom-scrollbar pb-1">
              {audioMemos.map((memo) => (
                <div
                  key={memo.id}
                  className={`p-3 bg-black border rounded-xl flex items-center justify-between transition ${
                    memo.status === 'MIRRORED_TO_VAULT'
                      ? 'border-emerald-500/10 hover:border-emerald-500/20'
                      : 'border-slate-900 hover:border-[#e840f3]/20 shadow-[0_0_10px_rgba(232,64,243,0.02)]'
                  }`}
                >
                  <div className="space-y-1.5 max-w-[70%]">
                    <div className="flex items-center gap-2">
                      <FileAudio className={`w-4 h-4 shrink-0 ${memo.status === 'MIRRORED_TO_VAULT' ? 'text-emerald-400' : 'text-[#e840f3]'}`} />
                      <span className="text-[10px] font-bold text-white uppercase truncate tracking-tight">{memo.title}</span>
                    </div>
                    
                    <div className="flex items-center gap-3 text-[8px] text-slate-500 uppercase">
                      <span>DURATION: <span className="text-slate-300 font-bold">{memo.duration}</span></span>
                      <span>SIZE: <span className="text-slate-300 font-bold">{memo.size}</span></span>
                      <span>DATE: <span className="text-slate-300 font-bold">{memo.date}</span></span>
                    </div>

                    {/* Simple Custom Player for saved local files */}
                    {memo.url && (
                      <div className="pt-2">
                        <audio src={memo.url} controls className="h-6 w-full opacity-60 hover:opacity-100 transition rounded" />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {memo.status === 'MIRRORED_TO_VAULT' ? (
                      <div className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-[7px] font-black tracking-widest uppercase flex items-center gap-1 shadow-[0_0_8px_rgba(16,185,129,0.1)]">
                        <Check className="w-3 h-3 text-emerald-400" />
                        <span>VAULT_CONFIRMED</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => mirrorToImperialVault(memo.id)}
                        disabled={vaultProcessing === memo.id}
                        className="py-1.5 px-2 bg-gradient-to-r from-[#e840f3] to-indigo-500 hover:opacity-95 text-black font-extrabold uppercase rounded text-[7px] tracking-widest cursor-pointer disabled:opacity-45"
                      >
                        {vaultProcessing === memo.id ? (
                          <span className="flex items-center gap-1"><RefreshCw className="w-2.5 h-2.5 animate-spin" />MIRRORING_SYS...</span>
                        ) : (
                          "MIRROR TO G-VAULT"
                        )}
                      </button>
                    )}

                    <div className="flex gap-2">
                      {memo.url && (
                        <a
                          href={memo.url}
                          download={memo.title}
                          title="Download Audio Direct Module"
                          className="p-1 px-[5px] bg-slate-900/40 hover:bg-slate-800 border border-slate-950 hover:border-slate-800 rounded text-[8px] text-slate-400 hover:text-white transition flex items-center gap-0.5 font-bold"
                        >
                          <Download className="w-3 h-3 text-slate-400" />
                          <span>DLL</span>
                        </a>
                      )}
                      
                      <button
                        onClick={() => deleteMemo(memo.id)}
                        title="Delete directive copy"
                        className="p-1 px-[5px] bg-slate-900/40 hover:bg-red-950/20 border border-slate-950 hover:border-red-500/30 rounded text-[8px] text-slate-400 hover:text-red-400 transition flex items-center gap-0.5 font-bold cursor-pointer"
                      >
                        <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-400" />
                        <span>DEL</span>
                      </button>
                    </div>
                  </div>

                </div>
              ))}
            </div>

            <div className="bg-[#00050c] p-3 rounded-xl border border-slate-950/80 text-[8px] text-slate-500 uppercase leading-relaxed flex items-center gap-1.5 select-none">
              <Shield className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
              <span>CloudSync-02 ENFORCED: ALL INHERENT RAW VOICE PIECES ARE SECURELY PROCESSED DIRECTLY VIA STANDARD MEDIA-STREAM PROTOCOLS.</span>
            </div>
          </div>

        </div>
      </div>

      {/* PROACTIVE PROSPECTING & STRIKE CAMPAIGN BUILDER */}
      <div id="campaign-builder-studio" className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="border-b border-slate-900 pb-4 mb-6">
          <div className="text-cyan-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Compass className="w-4 h-4" />
            <span>NEXORAX AUTONOMOUS PROSPECT MATRIX</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase mt-1">
            STRIKE CAMPAIGN & PROSPECT PORTAL
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1">
            Build bespoke outbound transactional templates geared directly towards closing high-value corporate partners. Instantly insert authorized checkout gateway files and register closed customer deals into our centralized Imperial ledger index.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          
          {/* Target input Form */}
          <div className="space-y-4 font-mono text-xs text-slate-400">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500">Contact Target Name</label>
                <input
                  type="text"
                  value={customLead.name}
                  onChange={(e) => setCustomLead({...customLead, name: e.target.value})}
                  placeholder="Alex Sterling"
                  className="bg-black border border-slate-800 rounded-xl p-2.5 text-white"
                />
              </div>
              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500">Target Enterprise</label>
                <input
                  type="text"
                  value={customLead.company}
                  onChange={(e) => setCustomLead({...customLead, company: e.target.value})}
                  placeholder="Apex Healthcare"
                  className="bg-black border border-slate-800 rounded-xl p-2.5 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500">Industry Sector</label>
                <input
                  type="text"
                  value={customLead.industry}
                  onChange={(e) => setCustomLead({...customLead, industry: e.target.value})}
                  placeholder="SME Healthcare"
                  className="bg-black border border-slate-800 rounded-xl p-2.5 text-white"
                />
              </div>
              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500">Est. Target Value (£)</label>
                <input
                  type="number"
                  value={customLead.revenueEst}
                  onChange={(e) => setCustomLead({...customLead, revenueEst: parseFloat(e.target.value) || 0})}
                  placeholder="2800000"
                  className="bg-black border border-slate-800 rounded-xl p-2.5 text-white"
                />
              </div>
            </div>

            <div className="bg-black/45 border border-slate-900 p-4 rounded-xl space-y-2.5">
              <span className="text-[9px] text-slate-500 font-bold block uppercase border-b border-slate-900 pb-1.5">EMBEDDED CONVERSION ASSET</span>
              <div className="text-[10px] text-slate-400 uppercase leading-relaxed font-bold break-all">
                Stripe Premium Link: <a href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline ml-1">https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00</a>
              </div>
              <div className="pt-1 text-[8.5px] text-[#e840f3] font-black uppercase flex items-center gap-1.5 select-none">
                <Check className="w-3.5 h-3.5" /> SECURE STRIPE PROTOCOL LOCKED INTENT
              </div>
            </div>

            <button
              onClick={handleGeneratePitch}
              className="w-full py-3.5 bg-[#00050e] border border-cyan-500/30 hover:bg-cyan-500 hover:text-black hover:border-cyan-500 text-cyan-400 font-extrabold uppercase rounded-xl transition duration-300 text-xs tracking-widest cursor-pointer"
            >
              COMPILE HIGH-CONVERSION OUTBOUND STRIKE PITCH
            </button>
          </div>

          {/* Generated Campaign result */}
          <div className="flex flex-col space-y-3 h-full">
            <span className="text-[8px] text-slate-500 font-mono font-black uppercase tracking-widest">COMPILED COPY-PASTE OUTBOUND TELEMETRY PITCH</span>
            
            <div className="relative h-full flex flex-col justify-between space-y-4">
              <div className="bg-black border border-slate-900 rounded-2xl p-4 font-mono text-[9.5px] text-slate-300 leading-relaxed uppercase whitespace-pre-wrap select-all min-h-64 h-full custom-scrollbar">
                {campaignResult || `[STANDBY]
Enter client lead parameters and click 'Compile Outbound Pitch' to generate highly persuasive campaign mandates pre-formatted for direct WhatsApp or email outreach to priority decision-makers. All templates feature Lead Director's certified Stripe billing links and corporate phone lines.`}
              </div>

              {campaignResult && (
                <div className="bg-[#02050f] border border-emerald-500/10 p-3 rounded-xl text-[9px] text-[#4af183] font-mono tracking-tight uppercase font-extrabold">
                  SECURE REGISTER_DEAL Handshake status: PENDING ACTIVATION FEE CHECK. REGISTRY CACHED.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* NEXORAX WEB RECONNAISSANCE & AI AUDIT NODE (NODE-11) */}
      <div id="NexoraX-recon-scraper-studio" className="bg-[#02050f] border border-slate-900 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="border-b border-slate-900 pb-4 mb-6">
          <div className="text-emerald-400 font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Globe className="w-4 h-4 animate-pulse" />
            <span>NEXORAX NEXORAX RECON MATRIX • NODE-11</span>
          </div>
          <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase mt-1">
            INTELLIGENCE SCRAPER & PERFORMANCE AUDITOR
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1">
            Siphon company metadata, locate email channels, and compile strategic performance upgrade reports directly from Mahmud Director's Office of the Director under international regulatory corridors.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Inputs Panel */}
          <div className="lg:col-span-5 space-y-5 font-mono text-xs text-slate-400">
            <span className="text-[9px] text-[#55f89c] font-black uppercase tracking-wider block">TARGET RECON CREDENTIALS</span>
            
            <div className="space-y-4">
              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500 text-[10px]">Company / Target Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={scraperTagName}
                    onChange={(e) => setScraperTagName(e.target.value)}
                    placeholder="e.g. Joe's Artisanal Bakeries"
                    className="w-full bg-[#00030a] border border-slate-900 focus:border-emerald-500/50 rounded-xl px-4 py-3 text-slate-200 outline-none text-xs transition placeholder:text-slate-700 font-bold"
                  />
                </div>
              </div>

              <div className="flex flex-col space-y-1.5">
                <label className="uppercase font-bold text-slate-500 text-[10px]">Website URL Resource Link</label>
                <div className="relative">
                  <input
                    type="text"
                    value={scraperTagLink}
                    onChange={(e) => setScraperTagLink(e.target.value)}
                    placeholder="https://artisanalbakeries.co.uk"
                    className="w-full bg-[#00030a] border border-slate-900 focus:border-emerald-500/50 rounded-xl px-4 py-3 text-slate-200 outline-none text-xs transition placeholder:text-slate-700 font-bold"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleTriggerScraper}
              disabled={scraperLoading}
              className={`w-full py-4 text-xs font-black tracking-widest uppercase rounded-xl transition duration-300 flex items-center justify-center gap-2 border cursor-pointer ${
                scraperLoading
                  ? "bg-slate-950 border-slate-900 text-slate-600 pointer-events-none"
                  : "bg-[#00050e] border-emerald-500/30 hover:bg-emerald-500 hover:text-black hover:border-emerald-500 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.1)] hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              }`}
            >
              {scraperLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>EXTRACTING INTELLIGENCE SIGNALS...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span>ROUTE RECON & DIAGNOSE WEBPAGE</span>
                </>
              )}
            </button>

            {/* Diagnostic Logs terminal screen */}
            <div className="border border-slate-900 rounded-2xl bg-black p-4 space-y-2 select-none h-60 flex flex-col justify-between overflow-hidden">
              <div className="space-y-1 text-[9px] uppercase leading-relaxed text-emerald-500/80 font-mono overflow-y-auto max-h-[190px] custom-scrollbar flex-1">
                <p className="text-zinc-500 font-extrabold border-b border-slate-900 pb-1 flex items-center gap-1">
                  <Terminal className="w-3 h-3 text-emerald-500" /> ACTIVE SCRAPER TELEMETRY OS:
                </p>
                {scraperLogs.length === 0 ? (
                  <p className="text-slate-700 italic pt-2">[STANDBY] WAITING FOR NETWORK INJECTION TARGET COMMANDS.</p>
                ) : (
                  scraperLogs.map((log, lIdx) => (
                    <p key={lIdx} className="tracking-tight text-slate-300">
                      {log}
                    </p>
                  ))
                )}
              </div>

              <div className="flex items-center justify-between border-t border-slate-900 pt-2 text-[8px] text-slate-600 uppercase font-black tracking-wider">
                <span>RECON RATE: 0.8s SLA</span>
                <span className={scraperLoading ? "text-amber-400 animate-pulse" : "text-slate-600"}>
                  {scraperLoading ? "● INDEXING CRAWLS" : "● PORT STANDBY"}
                </span>
              </div>
            </div>
          </div>

          {/* Analysis Board results */}
          <div className="lg:col-span-7 h-full flex flex-col">
            {scraperResult ? (
              <div className="bg-[#00030a] border border-emerald-500/20 rounded-2xl p-6 space-y-6 flex-1 shadow-[0_0_45px_rgba(16,185,129,0.05)]">
                
                {/* Header indicators */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-900 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 font-bold px-2 py-0.5 rounded text-[8.5px] uppercase tracking-wider font-mono">
                        {scraperResult.industry}
                      </span>
                      <span className="text-[10px] uppercase font-mono text-emerald-500 font-extrabold flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" /> SECURELY ENHANCED
                      </span>
                    </div>
                    <h4 className="text-lg font-black text-white tracking-tight uppercase">
                      {scraperResult.targetName}
                    </h4>
                    {scraperResult.targetUrl && (
                      <a href={scraperResult.targetUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-cyan-400 hover:underline inline-flex items-center gap-1 font-mono">
                        <Globe className="w-3.5 h-3.5 text-cyan-400" />
                        {scraperResult.targetUrl}
                      </a>
                    )}
                  </div>

                  {/* Overall Strategic Rating Card */}
                  <div className="bg-emerald-950/20 border-2 border-emerald-500/40 px-5 py-2.5 rounded-xl text-center shadow-[0_0_20px_rgba(16,185,129,0.15)] flex flex-col justify-center items-center shrink-0">
                    <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest block mb-0.5">ESTIMATED IMPROVEMNET</span>
                    <span className="text-2xl font-black text-emerald-400 tracking-tighter block font-mono">9.8/10</span>
                  </div>
                </div>

                {/* Email Signatures found */}
                <div>
                  <span className="text-[9px] text-slate-400 font-mono font-bold uppercase tracking-wider block mb-2.5">
                    HARVESTED ELECTRONIC CHANNELS
                  </span>
                  {scraperResult.emails.length === 0 ? (
                    <div className="text-[10px] text-zinc-500 font-mono uppercase bg-slate-950/40 rounded-xl px-4 py-2 border border-slate-900">
                      No active direct email coordinates siphoned. Employed automated local heuristics fallback.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {scraperResult.emails.map((email, eIdx) => (
                        <div key={eIdx} className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/20 rounded-xl px-3 py-1.5 font-mono text-[9.5px] text-emerald-300 font-black tracking-tight select-all">
                          <span>{email}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tactical Rating scale meters */}
                <div className="bg-slate-950 border border-slate-900 rounded-xl p-4 space-y-4">
                  <span className="text-[9px] text-[#4bf1a4] font-black uppercase tracking-wider block font-mono">
                    QUANTIFIED UPGRADE METRICS & RATINGS
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-bold text-slate-400">
                        <span>CONVERSION INCREASE</span>
                        <span className="text-emerald-400 text-xs font-black font-mono">+340%</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-emerald-400 h-1.5 rounded-full" style={{ width: "95%" }}></div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-bold text-slate-400">
                        <span>SLA RESPONSE DELAY</span>
                        <span className="text-[#00f0ff] text-xs font-black font-mono">&lt;0.8s</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-cyan-400 h-1.5 rounded-full" style={{ width: "98%" }}></div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-bold text-slate-400">
                        <span>OPERATIONAL YIELD</span>
                        <span className="text-[#e23ef5] text-xs font-black font-mono">+88%</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: "88%" }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Audit Content block */}
                <div className="space-y-3">
                  <div className="bg-slate-950/90 border border-slate-900 rounded-xl p-4 overflow-y-auto max-h-96 select-text text-slate-300 font-mono text-[10px] leading-relaxed whitespace-pre-wrap uppercase custom-scrollbar">
                    {scraperResult.analysis}
                  </div>
                </div>

                <div className="text-[8.5px] text-zinc-600 font-black tracking-normal uppercase text-center border-t border-slate-950 pt-3">
                  RECONNAISSANCE AUDITED AND REPLICATED IN THE NexoraX Resource Library UNDER MAHMOUD Director'S SECURITY COMMISSION.
                </div>

              </div>
            ) : (
              <div className="bg-black/40 border border-slate-900 rounded-2xl p-6 text-center select-none flex-1 flex flex-col justify-center items-center py-20">
                <Globe className="w-10 h-10 text-slate-700 mb-4 stroke-1 animate-pulse" />
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest font-black block">SYSTEM STANDBY</span>
                <p className="text-xs text-slate-600 font-mono uppercase max-w-sm mt-2">
                  Enter target company name and website URL in the fields on the left. Press "Route Recon & Diagnose Webpage" to parse domain files and receive automated strategic recommendations and rating enhancements.
                </p>
              </div>
            )}
          </div>

        </div>
      </div>

    </div>
  );
}
